import React, { useState, useEffect, useMemo } from 'react';

const TRANSLATIONS = {
  fa: {
    title: 'گالری آثار هنری',
    subtitle: 'مجموعه‌ای از آثار هنری خلق‌شده',
    allWorks: 'همه آثار',
    categoryLabel: 'دسته‌بندی',
    loading: 'در حال بارگذاری آثار...',
    error: 'خطا در خواندن اطلاعات گالری.',
    emptyMessage: 'تصویری در این دسته یافت نشد یا در gallery.json ثبتی وجود ندارد.',
    close: 'بستن',
    dir: 'rtl',
    langName: 'English',
    otherLang: 'en',
    imageLoadError: 'تصویر یافت نشد'
  },
  en: {
    title: 'Art Gallery',
    subtitle: 'A curated showcase of original artwork',
    allWorks: 'All Works',
    categoryLabel: 'Category',
    loading: 'Loading gallery items...',
    error: 'Failed to load gallery data.',
    emptyMessage: 'No images found in this category or gallery.json is empty.',
    close: 'Close',
    dir: 'ltr',
    langName: 'فارسی',
    otherLang: 'fa',
    imageLoadError: 'Image not found'
  }
};

export default function App() {
  const [lang, setLang] = useState('fa');
  const [selectedCategoryKey, setSelectedCategoryKey] = useState('all');
  const [activeModalImage, setActiveModalImage] = useState(null);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = TRANSLATIONS[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = t.dir;
  }, [lang, t.dir]);

  useEffect(() => {
    setLoading(true);
    fetch(`/gallery.json?t=${Date.now()}`)
      .then(res => {
        if (!res.ok) {
          throw new Error('Manifest not found');
        }
        return res.json();
      })
      .then(data => {
        const categoriesList = [];
        const itemsList = [];

        // پشتیبانی از ساختار آرایه‌ای با عنوان دوزبانه
        // [{ key: "cat1", title: { fa: "...", en: "..." }, images: ["img1.jpg"] }]
        data.forEach(cat => {
          categoriesList.push({
            key: cat.key,
            title: cat.title
          });

          const images = cat.images || [];
          images.forEach(fileName => {
            itemsList.push({
              id: `${cat.key}-${fileName}`,
              categoryKey: cat.key,
              categoryTitle: cat.title,
              fileName,
              title: fileName.replace(/\.[^/.]+$/, ""),
              src: `/images/${encodeURIComponent(fileName)}`
            });
          });
        });

        setGalleryCategories(categoriesList);
        setGalleryItems(itemsList);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading gallery manifest:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const filteredItems = useMemo(() => {
    if (selectedCategoryKey === 'all') {
      return galleryItems;
    }
    return galleryItems.filter(item => item.categoryKey === selectedCategoryKey);
  }, [galleryItems, selectedCategoryKey]);

  const toggleLanguage = () => {
    setLang(prev => (prev === 'fa' ? 'en' : 'fa'));
  };

  return (
    <div className={`container ${t.dir}`}>
      <header className="header">
        <div className="top-bar">
          <button className="lang-switcher" onClick={toggleLanguage}>
            🌐 {t.langName}
          </button>
        </div>
        <h1>{t.title}</h1>
        <p>{t.subtitle}</p>
      </header>

      {/* وضعیت لودینگ و خطا */}
      {loading && <div className="status-msg">{t.loading}</div>}
      {error && <div className="status-msg error">{t.error}</div>}

      {!loading && !error && (
        <>
          {/* دکمه‌های فیلتر دسته‌بندی با نام داینامیک دوزبانه */}
          <nav className="category-filter">
            <button
              className={selectedCategoryKey === 'all' ? 'active' : ''}
              onClick={() => setSelectedCategoryKey('all')}
            >
              {t.allWorks} ({galleryItems.length})
            </button>
            {galleryCategories.map(cat => {
              const count = galleryItems.filter(i => i.categoryKey === cat.key).length;
              const displayName = cat.title[lang] || cat.title.en || cat.key;
              return (
                <button
                  key={cat.key}
                  className={selectedCategoryKey === cat.key ? 'active' : ''}
                  onClick={() => setSelectedCategoryKey(cat.key)}
                >
                  {displayName} ({count})
                </button>
              );
            })}
          </nav>

          {/* شبکه تصاویر */}
          {filteredItems.length === 0 ? (
            <div className="empty-state">
              {t.emptyMessage}
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredItems.map(item => {
                const categoryName = item.categoryTitle[lang] || item.categoryTitle.en || item.categoryKey;
                return (
                  <div
                    key={item.id}
                    className="gallery-card"
                    onClick={() => setActiveModalImage(item)}
                  >
                    <div className="image-wrapper">
                      <img
                        src={item.src}
                        alt={item.title}
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('img-error');
                          e.target.parentElement.setAttribute('data-error', t.imageLoadError);
                        }}
                      />
                    </div>
                    <div className="card-info">
                      <h3>{item.title}</h3>
                      <span className="badge">{categoryName}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* مودال بزرگ‌نمایی */}
      {activeModalImage && (
        <div className="modal-backdrop" onClick={() => setActiveModalImage(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="close-btn"
              title={t.close}
              onClick={() => setActiveModalImage(null)}
            >
              ×
            </button>
            <img src={activeModalImage.src} alt={activeModalImage.title} />
            <div className="modal-caption">
              <h3>{activeModalImage.title}</h3>
              <span>
                {t.categoryLabel}: {activeModalImage.categoryTitle[lang] || activeModalImage.categoryTitle.en || activeModalImage.categoryKey}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
